import { useState, type FormEvent } from "react";
import {
  AppBar, Toolbar, InputBase, Box, IconButton, Avatar, Menu, MenuItem,
  ListItemIcon, ListItemText, Divider, Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useSidebarWidth } from "./Sidebar";
import { useLocalAuth, clearLocalAuthSession } from "@/localAuth";
import NotificationBell from "./NotificationBell";
import FeedbackDialog from "./FeedbackDialog";

interface Props {
  mode: "light" | "dark";
  onToggleMode: () => void;
  onMenuClick?: () => void;
}

export default function TopHeader({ mode, onToggleMode, onMenuClick }: Props) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { isAuthenticated: isLocalAuthenticated, displayName: localDisplayName } = useLocalAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const account = accounts[0];
  const displayName = isLocalAuthenticated ? localDisplayName : account?.name;
  const initials = displayName?.split(" ").map((p) => p[0]).slice(0, 2).join("") ?? "?";
  const sidebarWidth = useSidebarWidth();

  function handleLogout() {
    if (isLocalAuthenticated) {
      clearLocalAuthSession();
    } else {
      instance.logoutRedirect();
    }
  }

  function handleSearchSubmit(e: FormEvent) {
    e.preventDefault();
    if (search.trim()) navigate(`/assets?search=${encodeURIComponent(search.trim())}`);
  }

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="transparent"
      sx={{
        width: { xs: "100%", md: `calc(100% - ${sidebarWidth}px)` },
        ml: { xs: 0, md: `${sidebarWidth}px` },
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider"
      }}
    >
      <Toolbar sx={{ gap: { xs: 1, sm: 2 } }}>
        <IconButton onClick={onMenuClick} size="small" sx={{ display: { xs: "inline-flex", md: "none" } }}>
          <MenuIcon />
        </IconButton>

        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "action.hover",
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            flexGrow: 1,
            minWidth: 0,
            maxWidth: 480
          }}
        >
          <SearchIcon fontSize="small" sx={{ color: "text.secondary", mr: 1, flexShrink: 0 }} />
          <InputBase
            placeholder="Search assets, tags, serials…"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: "0.875rem", minWidth: 0 }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <IconButton onClick={() => setFeedbackOpen(true)} size="small" title="Send feedback">
          <ChatBubbleOutlineOutlinedIcon />
        </IconButton>

        <IconButton onClick={onToggleMode} size="small">
          {mode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
        </IconButton>

        <NotificationBell />

        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
          <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: "secondary.main" }}>
            {initials}
          </Avatar>
        </IconButton>

        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" fontWeight={600}>{displayName ?? "Signed-in user"}</Typography>
            {!isLocalAuthenticated && <Typography variant="caption" color="text.secondary">{account?.username}</Typography>}
          </Box>
          <Divider />
          <MenuItem onClick={() => navigate("/profile")}>
            <ListItemIcon><PersonOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>My profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Sign out</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </AppBar>
  );
}
